import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

/**
 * BaseBox Component
 * 
 * A standalone component that replicates TDS BaseBox behavior exactly.
 * This component has no dependency on UDS Box and provides all TDS Box props.
 * 
 * @example
 * <BaseBox between={3} vertical={3} horizontal={3}>
 *   <div>Child 1</div>
 *   <div>Child 2</div>
 * </BaseBox>
 * 
 * @example
 * <BaseBox tag="fieldset" between={2}>
 *   <legend>Form Section</legend>
 * </BaseBox>
 * 
 * @example
 * <BaseBox width={50} mdWidth={100} display="flex" justifyContent="center">
 *   Content
 * </BaseBox>
 */
const StyledBaseBox = styled.div`
  /* Width */
  ${props => props.width !== undefined ? `width: ${props.width}%;` : ''}
  
  /* Display */
  ${props => props.display ? `display: ${props.display};` : ''}
  
  /* Flexbox Properties */
  ${props => props.flexDirection ? `flex-direction: ${props.flexDirection};` : ''}
  ${props => props.flexWrap ? `flex-wrap: ${props.flexWrap};` : ''}
  ${props => props.justifyContent ? `justify-content: ${props.justifyContent};` : ''}
  ${props => props.alignItems ? `align-items: ${props.alignItems};` : ''}
  
  /* Inline prop - sets flex-direction to row */
  ${props => props.inline ? 'display: flex; flex-direction: row;' : ''}
  
  /* Between prop - spacing between children (0.5rem per unit) */
  ${props => props.between !== undefined ? `display: flex; flex-direction: column; gap: ${props.between * 0.5}rem;` : ''}
  ${props => props.inline && props.between !== undefined ? `flex-direction: row; gap: ${props.between * 0.5}rem;` : ''}
  
  /* Vertical and Horizontal padding (0.5rem per unit) */
  ${props => {
    if (props.vertical !== undefined && props.horizontal !== undefined) {
      return `padding: ${props.vertical * 0.5}rem ${props.horizontal * 0.5}rem;`;
    }
    if (props.vertical !== undefined) {
      return `padding-top: ${props.vertical * 0.5}rem; padding-bottom: ${props.vertical * 0.5}rem;`;
    }
    if (props.horizontal !== undefined) {
      return `padding-left: ${props.horizontal * 0.5}rem; padding-right: ${props.horizontal * 0.5}rem;`;
    }
    return '';
  }}
  
  /* Individual padding props (in pixels) */
  ${props => props.pb !== undefined ? `padding-bottom: ${props.pb}px;` : ''}
  ${props => props.pt !== undefined ? `padding-top: ${props.pt}px;` : ''}
  ${props => props.pr !== undefined ? `padding-right: ${props.pr}px;` : ''}
  ${props => props.pl !== undefined ? `padding-left: ${props.pl}px;` : ''}
  
  /* Margin props (in pixels) */
  ${props => props.mt !== undefined ? `margin-top: ${props.mt}px;` : ''}
  ${props => props.mr !== undefined ? `margin-right: ${props.mr}px;` : ''}
  ${props => props.mb !== undefined ? `margin-bottom: ${props.mb}px;` : ''}
  ${props => props.ml !== undefined ? `margin-left: ${props.ml}px;` : ''}
  
  /* Responsive props - medium breakpoint (992px) */
  @media screen and (max-width: 992px) {
    ${props => props.mdWidth !== undefined ? `width: ${props.mdWidth}%;` : ''}
    ${props => props.mdJustifyContent ? `justify-content: ${props.mdJustifyContent};` : ''}
    ${props => props.mdPadding ? `padding: ${props.mdPadding};` : ''}
    ${props => props.mdDisplay ? `display: ${props.mdDisplay};` : ''}
    ${props => props.mdFlexDirection ? `flex-direction: ${props.mdFlexDirection};` : ''}
  }
`;

const BaseBox = ({
  tag = 'div',
  children,
  className,
  style,
  ...props
}) => {
  // Handle fieldset element - remove default border and padding
  const elementStyle = tag === 'fieldset' 
    ? { border: 'none', padding: 0, margin: 0, ...style }
    : style;

  return (
    <StyledBaseBox
      as={tag}
      className={className}
      style={elementStyle}
      {...props}
    >
      {children}
    </StyledBaseBox>
  );
};

BaseBox.propTypes = {
  /** HTML element to render (default: 'div') */
  tag: PropTypes.string,
  /** Spacing between child elements (in units of 0.5rem) */
  between: PropTypes.number,
  /** Vertical padding (top + bottom, in units of 0.5rem) */
  vertical: PropTypes.number,
  /** Horizontal padding (left + right, in units of 0.5rem) */
  horizontal: PropTypes.number,
  /** Padding bottom (in pixels) */
  pb: PropTypes.number,
  /** Padding top (in pixels) */
  pt: PropTypes.number,
  /** Padding right (in pixels) */
  pr: PropTypes.number,
  /** Padding left (in pixels) */
  pl: PropTypes.number,
  /** Margin top (in pixels) */
  mt: PropTypes.number,
  /** Margin right (in pixels) */
  mr: PropTypes.number,
  /** Margin bottom (in pixels) */
  mb: PropTypes.number,
  /** Margin left (in pixels) */
  ml: PropTypes.number,
  /** Width percentage */
  width: PropTypes.number,
  /** CSS display property */
  display: PropTypes.string,
  /** CSS flex-direction property */
  flexDirection: PropTypes.string,
  /** CSS flex-wrap property */
  flexWrap: PropTypes.string,
  /** CSS justify-content property */
  justifyContent: PropTypes.string,
  /** CSS align-items property */
  alignItems: PropTypes.string,
  /** Sets flex-direction to row (shorthand) */
  inline: PropTypes.bool,
  /** Width percentage at medium breakpoint (992px) */
  mdWidth: PropTypes.number,
  /** Justify-content at medium breakpoint */
  mdJustifyContent: PropTypes.string,
  /** Padding at medium breakpoint */
  mdPadding: PropTypes.string,
  /** Display at medium breakpoint */
  mdDisplay: PropTypes.string,
  /** Flex-direction at medium breakpoint */
  mdFlexDirection: PropTypes.string,
  /** Additional CSS class name */
  className: PropTypes.string,
  /** Additional inline styles */
  style: PropTypes.object,
  /** Child elements */
  children: PropTypes.node,
};

export default BaseBox;

